import React, {useEffect, useRef, useState} from 'react';

import {DynamicPricingAuditService} from "../../service/DynamicPricingAuditService";
import DynamicPricingAuditTable from "./DynamicPricingAuditTable";

const auditService = new DynamicPricingAuditService();

// Matches the campaign-wide dialog's page size (DynamicPricingAuditDialog.js)
// so paging behaves the same way wherever this trail is viewed.
export const PRODUCT_AUDIT_PAGE_SIZE = 20;

// Pure query builder, exported so paging/target-id behaviour can be tested
// directly instead of mounting the component.
export const buildProductAuditQuery = (targetId, {first = 0, pageSize = PRODUCT_AUDIT_PAGE_SIZE} = {}) => ({
    target_id: targetId,
    limit: pageSize,
    offset: first,
});

// A product with no records has had no PRODUCT-scope changes -- this must
// stay false while a fetch is in flight, or a page that hasn't loaded yet
// would flash the same "nothing happened" message as a page that truly has
// no history.
export const isHistoryEmpty = (loading, rows) => !loading && (!rows || rows.length === 0);

// This product's own dynamic pricing history: a paged, target_id-scoped
// slice of the same audit trail the campaign-wide dialog shows.
//
// Reuses DynamicPricingAuditTable (lifted out of DynamicPricingAuditDialog)
// for the actual rendering -- same columns, same per-field expansion row --
// so a record never renders differently depending on where it's viewed.
// This component owns only the product-scoped fetch/paging and the honest
// empty-state copy; the filter toolbar in the campaign dialog (level/actor/
// date range, account-wide toggle) does not apply to a single product's
// already-scoped history and is deliberately not reused.
const ProductDynamicPricingAuditHistory = ({t, productId}) => {
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState(null);
    // Tracks the product the CURRENT `first`/`expandedRows` state belongs to.
    // ProductDetail keeps this component mounted across prev/next (no key
    // prop), so a page position browsed on product A must never leak into a
    // fetch for product B -- both because it's the wrong page, and because a
    // shorter product B page can come back empty and be misread by
    // isHistoryEmpty as "no history" when B in fact has records on page 1.
    const pagedProductIdRef = useRef(productId);

    useEffect(() => {
        if (!productId) return;
        const isProductSwitch = pagedProductIdRef.current !== productId;
        pagedProductIdRef.current = productId;
        if (isProductSwitch) {
            setExpandedRows(null);
            if (first !== 0) {
                // Reset paging and stop here -- the offset above still
                // belongs to the OLD product, so it must never be sent for
                // this one. Clearing it re-triggers this effect (dep: first)
                // and that re-run does the actual product-B fetch, at 0.
                setFirst(0);
                return;
            }
        }
        // Guards against an earlier fetch (a stale product or a stale page)
        // resolving after a later one and painting its response over the
        // current one -- same pattern as CompetitorRows.js.
        let active = true;
        setLoading(true);
        auditService.list(buildProductAuditQuery(productId, {first}))
            .then(data => { if (active) { setRows(data?.rows || []); setTotal(data?.total || 0); } })
            .catch(() => { if (active) { setRows([]); setTotal(0); } })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [productId, first]);

    return (
        <div style={{marginTop: 24}}>
            <h3 style={{margin: '0 0 8px'}}>{t('dynamic_pricing_audit.product_title')}</h3>
            <DynamicPricingAuditTable t={t} rows={rows} loading={loading}
                                      first={first} pageSize={PRODUCT_AUDIT_PAGE_SIZE} total={total}
                                      onPage={(e) => setFirst(e.first)}
                                      expandedRows={expandedRows} onRowToggle={(e) => setExpandedRows(e.data)}
                                      emptyMessage={isHistoryEmpty(loading, rows)
                                          ? t('dynamic_pricing_audit.product_empty')
                                          : t('dynamic_pricing_audit.empty')}/>
        </div>
    );
};

export default ProductDynamicPricingAuditHistory;
